/**
 * De prijs van een betaalde Mass DM (PPV): controleren en tonen.
 *
 * 04-10-2026 (Jordy): de prijs staat zichtbaar in het slot en is daar zelf in
 * te vullen (of: gratis), ook in het Mass DM-venster van Chats en, zolang het
 * bericht nog niet is verstuurd, vanuit de Agenda. Overal dezelfde regels als
 * de server bij plannen en versturen:
 *
 *   - FanCentro rekent in CREDITS (hele getallen, hoogstens 10.000), F2F in
 *     euro's;
 *   - minimumprijs voor een betaalde Mass DM: 500 credits / €5
 *     (`_validated_mass_ppv_minimum` in admin_conversations.py);
 *   - betaald alleen met content en alleen met het recht op betaalde Mass DM
 *     (dat recht controleert de eigenaar van het formulier).
 *
 * Eén Mass DM heeft één prijs per fan voor de hele bundel: de fan betaalt
 * één keer en opent dan alle items. FanCentro hangt de prijs aan het ene
 * bericht met alle bijlagen; F2F knipt een bundel van meer dan 6 items op en
 * zet de prijs alleen op het eerste deel (de rest gaat gratis mee), maar een
 * Mass DM heeft er hoogstens 6. Er is dus geen "totaal" over de items.
 *
 * Geen React: de tests voeren dit uit met node.
 */
import { prijsVoorPlatform } from './format'

/** De ondergrens voor BETAALDE content, de enige plek in het scherm (Jordy,
 *  01-10-2026; 05-10-2026 ook voor privéberichten in Chats). Dezelfde getallen
 *  als de server (`PPV_MIN_FC_CREDITS` / `PPV_MIN_F2F_EUR` in admin_conversations). */
export const PPV_MIN_FC_CREDITS = 500
export const PPV_MIN_F2F_EUR = 5
export const MASS_PPV_MIN_FC_CREDITS = PPV_MIN_FC_CREDITS
export const MASS_PPV_MIN_F2F_EUR = PPV_MIN_F2F_EUR
/** `_validated_fc_price_credits`: FanCentro hoogstens 10.000 credits. */
export const FC_MAX_CREDITS = 10_000

export function isF2f(platform: string | null | undefined): boolean {
  return String(platform ?? '').toLowerCase() === 'f2f'
}

/** "800" of "7,5" → getal; leeg of onzin → NaN. */
export function prijsAlsGetal(prijs: string | number | null | undefined): number {
  const tekst = String(prijs ?? '').trim().replace(',', '.')
  if (!tekst) return Number.NaN
  const n = Number(tekst)
  return Number.isFinite(n) ? n : Number.NaN
}

export function minimumVoor(platform: string | null | undefined): number {
  return isF2f(platform) ? MASS_PPV_MIN_F2F_EUR : MASS_PPV_MIN_FC_CREDITS
}

/** De eenheid naast het prijsveld. */
export function eenheidVoor(platform: string | null | undefined): string {
  return isF2f(platform) ? '€' : 'credits'
}

/** 11-10-2026 (Jordy): FanCentro rekent in credits, $1 = 100 credits. Alleen
 *  voor de uitleg bij het prijsveld; er wordt nergens mee gerekend. */
export const FC_CREDITS_PER_DOLLAR = 100

/** "500 credits (= $5)": een bedrag in credits met de omrekening erbij. */
function creditsMetDollars(n: number): string {
  return `${n.toLocaleString('en-US')} credits (= $${(n / FC_CREDITS_PER_DOLLAR).toLocaleString('en-US')})`
}

/**
 * 11-10-2026 (Jordy): de uitleg onder het prijsveld bij "Paid" — de eenheid,
 * de omrekening en het minimum, uit dezelfde getallen als de server.
 *   FanCentro: "Price in credits · $1 = 100 credits · $10 = 1,000 credits · minimum 500 credits (= $5)"
 *   F2F:       "Price in euro · minimum €5"
 */
export function prijsHulpTekst(platform: string | null | undefined): string {
  if (isF2f(platform)) return `Price in euro · minimum €${PPV_MIN_F2F_EUR}`
  const tien = 10 * FC_CREDITS_PER_DOLLAR
  return `Price in credits · $1 = ${FC_CREDITS_PER_DOLLAR} credits · $10 = ${tien.toLocaleString('en-US')} credits`
    + ` · minimum ${creditsMetDollars(PPV_MIN_FC_CREDITS)}`
}

/**
 * 11-10-2026 (Jordy): de melding van de contentkiezer bij "Paid"; null = in
 * orde. Leeg of onder het minimum is niet te versturen (de kiezer zet de knop
 * dan uit; de server weigert het ook). Met `massa` de woorden van een Mass DM.
 */
export function kiezerPrijsFout(
  platform: string | null | undefined, prijs: string | number | null | undefined, massa = false,
): string | null {
  const f2f = isF2f(platform)
  const n = prijsAlsGetal(prijs)
  if (!Number.isFinite(n) || n <= 0) {
    return f2f
      ? `Enter a price in euro — minimum €${PPV_MIN_F2F_EUR}.`
      : `Enter a price in credits — minimum ${creditsMetDollars(PPV_MIN_FC_CREDITS)}.`
  }
  const wat = massa ? 'a paid Mass DM' : 'paid content'
  const verder = massa ? 'Raise the price to use it.' : 'Raise the price to send it.'
  if (f2f && n < PPV_MIN_F2F_EUR) {
    return `Below the minimum: ${wat} on F2F must be at least €${PPV_MIN_F2F_EUR}. ${verder}`
  }
  if (!f2f && n < PPV_MIN_FC_CREDITS) {
    return `Below the minimum: ${wat} on FanCentro must be at least ${creditsMetDollars(PPV_MIN_FC_CREDITS)}. ${verder}`
  }
  return null
}

/** De melding bij een te lage prijs, per platform in zijn eigen munt. */
export function ppvMinimumTekst(platform: string | null | undefined): string {
  return isF2f(platform)
    ? `Paid Mass DM on F2F: at least €${MASS_PPV_MIN_F2F_EUR}`
    : `Paid Mass DM on FanCentro: at least ${MASS_PPV_MIN_FC_CREDITS} credits`
}

/**
 * Wat er mis is met de prijs van een BETAALDE Mass DM; leeg = in orde.
 * Gratis heeft geen prijs en is altijd goed.
 */
export function prijsFouten(platform: string | null | undefined, betaald: boolean, prijs: string | number | null | undefined): string[] {
  if (!betaald) return []
  const n = prijsAlsGetal(prijs)
  if (!Number.isFinite(n) || n <= 0) return ['Fill in a price, or choose Free.']
  if (isF2f(platform)) {
    return n < MASS_PPV_MIN_F2F_EUR ? [`${ppvMinimumTekst(platform)}.`] : []
  }
  if (n !== Math.trunc(n)) return ['FanCentro prices are whole credits (no decimals).']
  if (n > FC_MAX_CREDITS) return [`FanCentro: at most ${FC_MAX_CREDITS.toLocaleString('en-US')} credits.`]
  if (n < MASS_PPV_MIN_FC_CREDITS) return [`${ppvMinimumTekst(platform)}.`]
  return []
}

/** Een bedrag in de eenheid van het platform, zoals overal: "€15,00", "4,000 credits". */
export function bedragTekst(platform: string | null | undefined, prijs: string | number | null | undefined): string {
  const n = prijsAlsGetal(prijs)
  if (!Number.isFinite(n)) return isF2f(platform) ? '€—' : '— credits'
  return prijsVoorPlatform(n, isF2f(platform) ? 'f2f' : 'fancentro')
}

/**
 * De prijsregel voor slot, preview en bevestiging: wat één fan betaalt, en bij
 * meerdere items dat het één prijs voor de hele bundel is.
 */
export function prijsPerFanTekst(
  platform: string | null | undefined, betaald: boolean, prijs: string | number | null | undefined, aantal: number,
): string {
  if (aantal <= 0) return 'Text only'
  if (!betaald) return aantal === 1 ? 'Free' : `Free · ${aantal} items`
  const bedrag = `${bedragTekst(platform, prijs)} per fan`
  return aantal === 1 ? bedrag : `${bedrag} · ${aantal} items, one price`
}

/**
 * De bibliotheekprijs als voorstel: de hoogste `base_price` van de gekozen
 * items (één bundel, één prijs). null als de bibliotheek geen prijs heeft.
 * Het is een voorstel: het veld blijft vrij in te vullen.
 */
export function bibliotheekVoorstel(
  items: { base_price?: number | null }[] | null | undefined,
  platform?: string | null,
): number | null {
  let hoogste = 0
  for (const it of items ?? []) {
    const p = Number(it.base_price ?? 0)
    if (Number.isFinite(p) && p > hoogste) hoogste = p
  }
  if (hoogste <= 0) return null
  // 04-10-2026: de enige bibliotheekprijzen (5 FC-items op NT, 2–20) lijken
  // euro's, terwijl FC in credits rekent. Een voorstel onder het minimum van
  // het platform is dus geen bruikbaar voorstel: dan laten we het veld leeg.
  if (platform && hoogste < minimumVoor(platform)) return null
  return hoogste
}
